import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { marketplaceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  Calendar, 
  Target, 
  DollarSign, 
  ArrowLeft,
  Briefcase,
  Clock,
  RefreshCw,
  Package,
  Globe,
  Zap,
  Users
} from 'lucide-react';

function MyListingsPage() {
  var navigate = useNavigate();
  var auth = useAuth();
  var isBuyer = auth.isBuyer;

  var listingsState = useState([]);
  var listings = listingsState[0];
  var setListings = listingsState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var retryCountState = useState(0);
  var retryCount = retryCountState[0];
  var setRetryCount = retryCountState[1];

  useEffect(function() {
    if (isBuyer) {
      fetchListings();
    }
  }, [isBuyer]);

  function fetchListings(isRetry) {
    setLoading(true);
    if (!isRetry) {
      setError(null);
      setRetryCount(0);
    }
    
    marketplaceAPI.getMyListings()
      .then(function(response) {
        if (response.data.success) {
          var campaigns = response.data.data || [];
          // Calculate totals from offerings
          var processed = campaigns.map(function(campaign) {
            var offerings = campaign.offering || [];
            var totalTarget = 0;
            var totalBudget = 0;
            for (var i = 0; i < offerings.length; i++) {
              var target = parseInt(offerings[i].target) || 0;
              var cpl = parseFloat(offerings[i].cpl) || 0;
              totalTarget += target;
              totalBudget += cpl * target;
            }
            return {
              ...campaign,
              total_target: totalTarget > 0 ? totalTarget : campaign.total_target,
              total_budget: totalBudget > 0 ? totalBudget : campaign.total_budget
            };
          });
          setListings(processed);
          setError(null);
        } else {
          setError(response.data.error || 'Failed to fetch campaigns');
        }
      })
      .catch(function(err) {
        console.error('Failed to fetch campaigns:', err);
        if (!isRetry && retryCount < 1) {
          setRetryCount(retryCount + 1);
          setTimeout(function() {
            fetchListings(true);
          }, 2000);
          return;
        }
        setError('Failed to connect to server. Please click Refresh to try again.');
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  }

  function formatCurrency(amount) {
    if (!amount && amount !== 0) return 'N/A';
    return '$' + parseFloat(amount).toLocaleString();
  }

  function formatCampaignId(id) {
    if (!id) return 'N/A';
    var idStr = String(id);
    if (idStr.length < 6) return idStr;
    return idStr.substring(0, 2) + 'xxx' + idStr.slice(-4);
  }

  function formatGeoList(geoList) {
    if (!geoList || geoList.length === 0) return 'N/A';
    if (geoList.length <= 2) return geoList.join(', ');
    return geoList.slice(0, 2).join(', ') + ' +' + (geoList.length - 2) + ' more';
  }

  function renderOfferingItems(offerings) {
    if (!offerings || !Array.isArray(offerings) || offerings.length === 0) {
      return <p className="text-slate-500 text-sm">No offering details available</p>;
    }

    var rows = [];
    for (var i = 0; i < offerings.length; i++) {
      var item = offerings[i];
      var cpl = parseFloat(item.cpl) || 0;
      var target = parseInt(item.target) || 0;
      var remaining = item.deficit !== undefined ? parseInt(item.deficit) : target;
      // Calculate delivered as target minus remaining
      var delivered = target - remaining;
      if (delivered < 0) delivered = 0;
      var value = cpl * target;
      
      rows.push(
        <tr key={i} className="hover:bg-slate-50">
          <td className="px-4 py-3">
            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
              {item.lead_type}
            </Badge>
          </td>
          <td className="px-4 py-3 text-right font-medium text-slate-900">
            {target}
          </td>
          <td className="px-4 py-3 text-right text-emerald-600 font-medium">
            {delivered}
          </td>
          <td className="px-4 py-3 text-right text-amber-600">
            {remaining}
          </td>
          <td className="px-4 py-3 text-right text-slate-600">
            {formatCurrency(cpl)}
          </td>
          <td className="px-4 py-3 text-right font-semibold text-blue-600">
            {formatCurrency(value)}
          </td>
        </tr>
      );
    }
    return rows;
  }

  function renderCampaignCard(campaign, index) {
    var offerings = campaign.offering || [];
    var geoList = campaign.geo || [];
    var totalTarget = campaign.total_target || 0;
    var totalBudget = campaign.total_budget || 0;
    
    // Calculate total delivered as sum of (target - remaining) for each offering
    var totalDelivered = 0;
    for (var i = 0; i < offerings.length; i++) {
      var target = parseInt(offerings[i].target) || 0;
      var remaining = offerings[i].deficit !== undefined ? parseInt(offerings[i].deficit) : target;
      var delivered = target - remaining;
      if (delivered > 0) totalDelivered += delivered;
    }
    
    // Also calculate totalTarget if not provided
    if (!totalTarget) {
      for (var i = 0; i < offerings.length; i++) {
        totalTarget += parseInt(offerings[i].target) || 0;
      }
    }
    
    var statusClass = 'bg-slate-100 text-slate-700 border-slate-200';
    var status = (campaign.status || '').toLowerCase();
    if (status === 'active' || status === 'live') {
      statusClass = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    } else if (status === 'pending' || status === 'draft') {
      statusClass = 'bg-amber-100 text-amber-700 border-amber-200';
    }
    
    return (
      <AccordionItem key={index} value={'campaign-' + index} className="border rounded-lg mb-4 overflow-hidden">
        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50 [&[data-state=open]]:bg-orange-50">
          <div className="flex items-center justify-between w-full pr-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-orange-600" />
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">
                    {campaign.campaign_name || 'Untitled Campaign'}
                  </span>
                  <Badge className={statusClass}>
                    {campaign.status || 'Unknown'}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                  <span className="font-mono text-xs">CID: {formatCampaignId(campaign.id)}</span>
                  <span className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    {totalTarget} leads
                  </span>
                  <span className="flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    {offerings.length} types
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right mr-4">
              <div className="text-xs text-slate-500">Total Budget</div>
              <div className="text-lg font-bold text-blue-600">{formatCurrency(totalBudget)}</div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 mt-2">
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-slate-500 mb-1">
                <Briefcase className="w-3 h-3 mr-1" />
                Campaign Type
              </div>
              <div className="font-semibold text-slate-900">{campaign.type || 'N/A'}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-slate-500 mb-1">
                <Target className="w-3 h-3 mr-1" />
                Total Target
              </div>
              <div className="font-semibold text-slate-900">{totalTarget} leads</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-slate-500 mb-1">
                <Users className="w-3 h-3 mr-1" />
                Total Delivered
              </div>
              <div className="font-semibold text-emerald-700">{totalDelivered} leads</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-slate-500 mb-1">
                <DollarSign className="w-3 h-3 mr-1" />
                Total Budget
              </div>
              <div className="font-semibold text-blue-700">{formatCurrency(totalBudget)}</div>
            </div>
          </div>

          {/* Regions */}
          {geoList.length > 0 && (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center text-xs text-slate-500 mb-2">
                <Globe className="w-3 h-3 mr-1" />
                Target Regions
              </div>
              <div className="flex flex-wrap gap-1">
                {geoList.map(function(geo, i) {
                  return (
                    <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                      {geo}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Offerings Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-slate-700">Lead Type</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">Target</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">Delivered</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">Remaining</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">CPL</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {renderOfferingItems(offerings)}
              </tbody>
            </table>
          </div>

          {/* View Details Button */}
          <div className="mt-4 flex justify-end">
            <Button 
              onClick={function() { navigate('/campaign/' + campaign.id); }}
              className="bg-orange-500 hover:bg-orange-600"
            >
              View Full Details
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  }

  function renderSkeletons() {
    var items = [];
    for (var i = 0; i < 3; i++) {
      items.push(
        <Card key={i}>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-8 w-full" />
          </CardContent>
        </Card>
      );
    }
    return items;
  }

  function renderListings() {
    if (loading) {
      return renderSkeletons();
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">Failed to load campaigns</h3>
          <p className="text-slate-500 mb-4">{error}</p>
          <Button variant="outline" onClick={fetchListings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      );
    }

    if (listings.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-orange-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">No campaigns yet</h3>
          <p className="text-slate-500 mb-4">
            Your campaign listings will appear here once created.
          </p>
          <Button onClick={function() { navigate('/marketplace/sellers'); }} className="bg-orange-500 hover:bg-orange-600">
            Browse Sellers
          </Button>
        </div>
      );
    }

    var items = [];
    for (var i = 0; i < listings.length; i++) {
      items.push(renderCampaignCard(listings[i], i));
    }
    return (
      <Accordion type="single" collapsible className="w-full">
        {items}
      </Accordion>
    );
  }

  // Calculate totals for stats
  function calculateTotals() {
    var totalTarget = 0;
    var totalBudget = 0;
    var totalDelivered = 0;
    for (var i = 0; i < listings.length; i++) {
      var campaignTarget = listings[i].total_target || 0;
      totalBudget += listings[i].total_budget || 0;
      var offerings = listings[i].offering || [];
      
      // Calculate target and delivered from offerings if total_target not provided
      var offeringTarget = 0;
      for (var j = 0; j < offerings.length; j++) {
        var target = parseInt(offerings[j].target) || 0;
        var remaining = offerings[j].deficit !== undefined ? parseInt(offerings[j].deficit) : target;
        var delivered = target - remaining;
        if (delivered > 0) totalDelivered += delivered;
        offeringTarget += target;
      }
      totalTarget += campaignTarget || offeringTarget;
    }
    return { totalTarget: totalTarget, totalBudget: totalBudget, totalDelivered: totalDelivered };
  }

  if (!isBuyer) {
    return (
      <Layout>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Access Denied</h2>
            <p className="text-slate-500">This page is only available for buyers.</p>
          </div>
        </div>
      </Layout>
    );
  }

  var totals = calculateTotals();

  return (
    <Layout>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="my-listings-page">
        <Button variant="ghost" className="mb-6" onClick={function() { navigate('/marketplace/sellers'); }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Browse Sellers
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">My Campaigns</h1>
            <p className="text-slate-600">
              View and manage your campaign listings
            </p>
          </div>
          <Button variant="outline" onClick={fetchListings} disabled={loading}>
            <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </Button>
        </div>

        {/* Stats Summary */}
        {!loading && !error && listings.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Active Campaigns</div>
                  <div className="text-2xl font-bold text-slate-900">{listings.length}</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Total Target Leads</div>
                  <div className="text-2xl font-bold text-slate-900">{totals.totalTarget}</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Total Budget</div>
                  <div className="text-2xl font-bold text-emerald-600">{formatCurrency(totals.totalBudget)}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Campaigns List */}
        <div className="space-y-6">
          {renderListings()}
        </div>
      </div>
    </Layout>
  );
}

export default MyListingsPage;
